import * as React from "react";
import {useEffect, useState} from "react";
import {searchBarFindByTypes} from "./SearchBarConstants";
import {Loader} from "../Shared/Components/Loader";
import {isNullOrWhitespace} from "../Shared/Store/Functions";
import {FindByTypes} from "../TripsFilter/TripsFilterTypes";
import moment = require("moment");
import {ViewAsTypes} from "../TripDashboard/TripDashboardTypes";
import { usePolicies } from "../Shared/Permissions/PolicyContext";
import { Policies, PoliciesStatus } from "../Shared/Permissions/Policies";

const Noty = require('noty');

export interface SearchViewModel {
    hasDispatch: boolean,
    findByType: string,
    startDate: string,
    endDate: string,
    orderStatus: string,
    tripStatus: string,
    pageSize: number,
    viewAsType: string,
    supportNestedTripView: boolean,
    tripTypes: string
}

declare const SearchViewModel: SearchViewModel;

export const SearchBar = () => {
    const [initialLoad, setInitialLoad] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [findByType, setFindByType] = useState("");
    const [keyword, setKeyword] = useState("");
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    const { policies } = usePolicies();

    useEffect(() => {
        if (initialLoad && typeof (SearchViewModel) != null) {
            setFindByType(SearchViewModel.findByType);
            setInitialLoad(false);
        }

        window.addEventListener('resize', () => {
            setWindowWidth(window.innerWidth);
        });
    }, []);

    function userHasAnyRequiredPolicy(
        required: Policies[] | undefined,
        userPolicies: PoliciesStatus
      ) {
        // If no particular policy is required, assume the user can see it
        if (!required || required.length === 0) {
          return true;
        }
        // Check if the user has at least one of the required policies
        return required.some((policy) => userPolicies[policy] === true);
      }
      
    function handleSaveChanges() {
        setIsLoading(true);
        let url = `/${SearchViewModel.hasDispatch ? 'TripDashboard' : 'OrderDashboard'}?findBy=${findByType}&keyword=${keyword}`;

        if (windowWidth <= 768) {
            switch (findByType) {
                case "OrderNumber":
                    url = `/OrderDashboard?findBy=${findByType}&keyword=${keyword}&ViewAsType=${ViewAsTypes.Tiles}`;
                    break;
                case "DriverName":
                    url = `/${SearchViewModel.hasDispatch ? 'TripDashboard' : 'OrderDashboard'}?findBy=${findByType}&keyword=${keyword}&ViewAsType=${ViewAsTypes.Tiles}`;
                    break;
                case "PatientName":
                    url = `/OrderDashboard?findBy=${findByType}&keyword=${keyword}&ViewAsType=${ViewAsTypes.Tiles}`;
                    break;
                case "TripNumber":
                    url = `/${SearchViewModel.hasDispatch ? 'TripDashboard' : 'OrderDashboard'}?findBy=${findByType}&keyword=${keyword}&ViewAsType=${ViewAsTypes.Tiles}`;
                    break;
                case "OrderReference":
                    url = `/OrderDashboard?findBy=${findByType}&keyword=${keyword}&ViewAsType=${ViewAsTypes.Tiles}`;
                    break;
                case "DeliveryNumber":
                    url = `/OrderDashboard?findBy=${findByType}&keyword=${keyword}&ViewAsType=${ViewAsTypes.Tiles}`;
                    break;
            }
        } else {
            switch (findByType) {
                case "OrderNumber":
                    url += `&ViewAsType=${SearchViewModel.viewAsType ? SearchViewModel.viewAsType : ViewAsTypes.Table}`;
                    break;
                case "DriverName":
                    url += `&ViewAsType=${SearchViewModel.viewAsType ? SearchViewModel.viewAsType : (SearchViewModel.supportNestedTripView ? ViewAsTypes.NestedTable : ViewAsTypes.Table)}`;
                    break;
                case "PatientName":
                    url += `&ViewAsType=${SearchViewModel.viewAsType ? SearchViewModel.viewAsType : ViewAsTypes.Table}`;
                    break;
                case "TripNumber":
                    url += `&ViewAsType=${SearchViewModel.viewAsType ? SearchViewModel.viewAsType : (SearchViewModel.supportNestedTripView ? ViewAsTypes.NestedTable : ViewAsTypes.Table)}`;
                    break;
                case "OrderReference":
                    url += `&ViewAsType=${SearchViewModel.viewAsType ? SearchViewModel.viewAsType : ViewAsTypes.Table}`;
                    break;
                case "DeliveryNumber":
                    url += `&ViewAsType=${SearchViewModel.viewAsType ? SearchViewModel.viewAsType : ViewAsTypes.Table}`;
                    break;
            }
        }

        if (!isNullOrWhitespace(SearchViewModel.startDate) && !isNullOrWhitespace(SearchViewModel.endDate)) {
            url += `&StartDate=${moment(SearchViewModel.startDate).format('YYYY-MM-DD')}&EndDate=${moment(SearchViewModel.endDate).format('YYYY-MM-DD')}`
        }

        if (!isNullOrWhitespace(SearchViewModel.tripStatus)) {
            let statuses = SearchViewModel.tripStatus.split(',');

            if (statuses) {
                statuses = statuses.filter(t => {
                    return !isNullOrWhitespace(t)
                });
                statuses.forEach(status => {
                    url += `&SelectedTripStatuses=${status}`;
                })
            }
        }

        if (!isNullOrWhitespace(SearchViewModel.orderStatus)) {
            let statuses = SearchViewModel.orderStatus.split(',');

            if (statuses) {
                statuses = statuses.filter(t => {
                    return !isNullOrWhitespace(t)
                });
                statuses.forEach(status => {
                    url += `&SelectedOrderStatuses=${status}`;
                })
            }
        }

        if (SearchViewModel.pageSize != null) {
            url += `&PageSize=${SearchViewModel.pageSize}`
        }

        window.location.href = url;
    }

    function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
        e.preventDefault();

        if (validateForm()) {
            handleSaveChanges();
        }
    }

    function validateForm(): boolean {
        if (isNullOrWhitespace(keyword)) {
            let noty = new Noty({
                text: 'Please enter a value to look up by.',
                type: "error",
                theme: 'metroui',
                timeout: 3000
            }).show();

            return false;
        } else if(isNullOrWhitespace(findByType)) {
            let noty = new Noty({
                text: 'Please enter a search type.',
                type: "error",
                theme: 'metroui',
                timeout: 3000
            }).show();

            return false;
        } else {
            return true;
        }
    }

    return (
        <>
            {
                policies.ViewSearchBar && (
                    <form className={`form-inline my-2 my-lg-0 ${windowWidth > 768 && 'mr-4'}`}
                        onSubmit={(e) => handleSubmit(e)}>
                        {
                            isLoading ?
                                <Loader/>
                                :
                                null
                        }
                        {
                            windowWidth > 768 ?
                                <div className="input-group">
                                    <div className="input-group-prepend">
                                        <span className="input-group-text">Find By</span>
                                    </div>
                                    <select className="form-control" name="FindByType"
                                            onChange={(e) => setFindByType(e.target.value)}
                                            value={findByType}>
                                        <option value="" key="">Select a search type..</option>

                                        {
                                            searchBarFindByTypes.map(t => {                                   
                                                if(!userHasAnyRequiredPolicy(t.requiresAnyPolicy, policies)) {
                                                    return null;
                                                }
                                                
                                                return (
                                                    <option value={t.key}
                                                            key={t.key}>{t.value}</option>
                                                )
                                            })
                                        }
                                    </select>
                                    <input type="text"
                                        className="form-control"
                                        defaultValue={keyword}
                                        onChange={(e) => setKeyword(e.target.value)}
                                        placeholder="Enter text..."
                                        aria-label="Order and Trip Search Input"
                                        name="SearchTerm"/>
                                    <div className="input-group-append">
                                        <button className="btn btn-outline-secondary" type="submit">Submit</button>
                                    </div>
                                </div>
                                :
                                <div className="w-100">
                                    <div className="form-group">
                                        <div className="input-group">
                                            <div className="input-group-prepend">
                                                <span className="input-group-text">Find By</span>
                                            </div>
                                            <select className="form-control" name="FindByType"
                                                    onChange={(e) => setFindByType(e.target.value)}
                                                    value={findByType}>
                                                <option value="" key="">Select a search type..</option>

                                                {
                                                    searchBarFindByTypes.map(t => {
                                                        if(!userHasAnyRequiredPolicy(t.requiresAnyPolicy, policies)) {
                                                            return null;
                                                        }

                                                        return (
                                                            <option value={t.key} key={t.key}>{t.value}</option>
                                                        )
                                                    })
                                                }
                                            </select>
                                        </div>
                                    </div>
                                    <div className="form-group">
                                        <div className="input-group">
                                            <input type="text"
                                                className="form-control"
                                                defaultValue={keyword}
                                                onChange={(e) => setKeyword(e.target.value)}
                                                placeholder="Enter text..."
                                                aria-label="Order and Trip Search Input"
                                                name="SearchTerm"/>
                                            <div className="input-group-append">
                                                <button className="btn btn-outline-secondary" type="submit">Submit</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                        }
                    </form>
                )
            }
        </>
        
    )
};